import React, { useEffect, useRef } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import 'mathlive';
import { convertLatexToMathMl } from 'mathlive';

// mathlive registers <math-field> as a custom element; tell TSX it exists.
declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'math-field': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        ref?: React.Ref<any>;
      };
    }
  }
}

/** Marks a chunk of content as an equation, and carries the source LaTeX for re-editing. */
export const MATH_CLASS = 'lo-math';

/**
 * Wraps an equation for storage: MathML so browsers render it natively with no
 * library on the student side, plus the original LaTeX so it can be edited again.
 */
export const buildMathHtml = (latex: string): string => {
  const mathml = convertLatexToMathMl(latex);
  const escaped = latex.replace(/"/g, '&quot;');
  return `<span class="${MATH_CLASS}" data-latex="${escaped}" contenteditable="false">${mathml}</span>`;
};

interface MathEditorProps {
  open: boolean;
  /** LaTeX to start from, when editing an existing equation. */
  initialLatex?: string;
  onClose: () => void;
  onInsert: (latex: string) => void;
}

const MathEditor: React.FC<MathEditorProps> = ({ open, initialLatex = '', onClose, onInsert }) => {
  const fieldRef = useRef<any>(null);

  useEffect(() => {
    if (!open) return;
    // The element is only mounted while the dialog is open, so seed it each time.
    const timer = window.setTimeout(() => {
      if (fieldRef.current) {
        fieldRef.current.value = initialLatex;
        fieldRef.current.focus();
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [open, initialLatex]);

  const handleInsert = () => {
    const latex = fieldRef.current?.value?.trim();
    if (latex) {
      onInsert(latex);
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Equation</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Type or use the on-screen keyboard. Equations are stored so students see them rendered.
        </Typography>
        <math-field
          ref={fieldRef}
          style={{
            width: '100%',
            minHeight: 80,
            fontSize: '1.4rem',
            padding: '12px',
            border: '1px solid rgba(0,0,0,0.23)',
            borderRadius: 4,
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleInsert} variant="contained">
          Insert
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MathEditor;
